import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { addHours, format, isValid, parse } from 'date-fns';
import { Form } from '@unform/web';
import pt from 'date-fns/locale/pt';
import { useNavigate } from 'react-router-dom';

import config from 'config/environment';

import { show } from 'store/modules/dialog/actions';
import {
  Permissao,
  Role,
  StatusSolicitacao,
  TipoSolicitacaoTelao,
} from 'utils/variables';
import api from 'services/api';
import AppError from 'utils/AppError';
import { Usuario } from 'pages/@types';
import { usePermit } from 'utils/hooks/usePermit';
import { useModal } from 'hooks/modal';
import NoSolicitacaoPromo from 'assets/Fill out-pana.svg';

import Card from 'components/Card';
import Button from 'components/Button';
import Pagination from 'components/Pagination';
import Loader from 'components/ContentLoader/GenericLoader';
import Widget from 'components/Widget';
import NotFound from 'components/NotFound';
import Avatar from 'components/Avatar';
import Flag from 'components/Flag';
import Input from 'components/Input';
import Tooltip from 'components/Tooltip';
import DropDownMenu from 'components/DropDownMenu';
import ProgressBar from 'components/ProgressBar';

import {
  FiBookOpen,
  FiCheckCircle,
  FiCopy,
  FiEdit,
  FiEye,
  FiPlus,
  FiSearch,
  FiTrash2,
  FiUpload,
} from 'react-icons/fi';

import { Table } from 'styles/global';
import { Container, NovoInfo, TeloesLinks, WidgetContent } from './styles';

interface TelaoPadrao {
  id: number;
  download_url: string;
  nomearquivo: string;
}
interface SolicitacaoTelao {
  id: number;
}
export interface SolicitacaoPromo {
  id: number;
  nome: string;
  status: string;
  telefone?: string;
  eventorpc: string;
  datainicioveiculacao: string;
  datainicio: string;
  emissora: string;
  solicitante: Usuario;
  telao: 'NA' | 'padrao' | 'outroformato' | 'novacriacao';
  solicitacaotelao: SolicitacaoTelao;
  posevento: string;
}

interface Workflow {
  emprocesso: number;
  enviada: number;
}
export default function SolicitacaoPromo() {
  // react-router-dom
  const navigate = useNavigate();
  /**
   * USE STATE
   */
  const [workflow, setWorkflow] = useState({
    emprocesso: 0,
    enviada: 0,
  } as Workflow);
  const [filtro, setFiltro] = useState('');
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [status, setStatus] = useState(StatusSolicitacao.EMPROCESSO);
  const [solicitacoes, setSolicitacoes] = useState<SolicitacaoPromo[]>([]);
  const [selected, setSelected] = useState({} as SolicitacaoPromo);
  /**
   * REDUX
   */
  const dispatch = useDispatch();
  const { showModal, hideModal } = useModal();
  const hasSolPromoOutros = usePermit([], [Permissao.SOLPROMOOUTROS]);
  const hasUploadTelao = usePermit([Role.PROMO], []);

  const { profile } = useSelector((state: any) => state.usuario);
  const { callback: callbackDialog } = useSelector(
    (state: any) => state.dialog,
  );

  /**
   * USE CALLBACK
   */

  const handleDelete = useCallback(
    s => {
      setSelected(s);
      const optionBase = {
        type: 'question',
        action: 'delete',
        contentHTML: `Remover <b>${s.nome}</b> ?`,
        confirmButtonText: 'CONFIRMAR',
        callback: {
          url: `/solicitacaopromo/${s.id}`,
          method: api.delete,
        },
        haveCallback: true,
        successMessage: 'Solicitação removida!',
        cancelButtonText: 'NÃO',
        showCancelButton: false,
        showConfirmButton: true,
        showCloseButton: true,
      };
      dispatch(show(optionBase));
    },
    [dispatch],
  );

  const handleClick = useCallback(s => {
    setStatus(s);
    setPage(1);
  }, []);

  // const handleEdit = useCallback(sp => {
  //   navigate(`/solicitacaopromo/${sp.id}`);
  // }, []);

  const handleCopy = useCallback(
    sp => {
      const novaSolicitacaoPromo = {
        ...sp,
        status: StatusSolicitacao.EMPROCESSO,
        datainicioveiculacao: addHours(new Date(sp.datainicioveiculacao), 3),
        datafimveiculacao: sp.datafimveiculacao
          ? addHours(new Date(sp.datafimveiculacao), 3)
          : null,
      };
      delete novaSolicitacaoPromo.id;
      delete novaSolicitacaoPromo.created_at;
      delete novaSolicitacaoPromo.download_url;
      delete novaSolicitacaoPromo.updated_at;
      delete novaSolicitacaoPromo.deleted_at;
      delete novaSolicitacaoPromo.solicitante;
      delete novaSolicitacaoPromo.solicitacaotelao;
      delete novaSolicitacaoPromo.telao;

      const optionBase = {
        type: 'question',
        contentHTML: `Duplicar <b>${sp.nome}</b> ?`,
        confirmButtonText: 'CONFIRMAR',
        inputType: 'text',
        inputValue: sp.nome,
        callback: {
          url: `/solicitacaopromo`,
          method: api.post,
          data: novaSolicitacaoPromo,
          action: 'duplicapromo',
        },
        haveCallback: true,
        successMessage: `Solicitação ${sp.nome} duplicada!`,
        cancelButtonText: 'NÃO',
        showCancelButton: false,
        showConfirmButton: true,
        showCloseButton: true,
      };
      setSelected(sp);
      dispatch(show(optionBase));
    },
    [dispatch],
  );

  const handleActionSelect = useCallback(
    (sp, action) => {
      switch (action.value) {
        case 1:
          handleCopy(sp);
          break;
        case 2:
          handleDelete(sp);
          break;
        default:
          break;
      }
    },
    [handleCopy, handleDelete, navigate],
  );

  const handleSubmitTelaoPadrao = useCallback(
    async (solicitacaoId: number, telao: string) => {
      try {
        const response = await api.patch(
          `/solicitacaopromo/${solicitacaoId}/telao`,
          {
            telao: TipoSolicitacaoTelao.PADRAO,
          },
        );
        if (response && response.data && response.data.message === 'success') {
          setSolicitacoes(prevState =>
            prevState.map(solicitacao =>
              solicitacao.id === solicitacaoId
                ? {
                    ...solicitacao,
                    telao: 'padrao',
                  }
                : solicitacao,
            ),
          );
          toast.success('Telão Padrão associado à Solicitação de Promo');
        } else {
          toast.error('Erro ao associar Telão Padrão à Solicitação de Promo');
        }
      } catch (err) {
        console.log(err);
        if (err instanceof Error) {
          toast.error(err.message);
        } else {
          const appError = err as AppError;
          toast.error(appError.message);
        }
      } finally {
        hideModal();
      }
    },
    [hideModal],
  );

  const renderLinksTeloes = useCallback(
    (teloes, solicitacao) => {
      return (
        <TeloesLinks>
          {teloes.map((t: TelaoPadrao) => (
            <div>
              <h4>{t.nomearquivo}</h4>
              <div>
                <Button
                  onClick={() => {
                    window.open(
                      t.download_url,
                      '_blank',
                      'noopener,noreferrer',
                    );
                  }}
                >
                  ASSISTIR / BAIXAR
                </Button>
                <Button
                  onClick={() => {
                    handleSubmitTelaoPadrao(solicitacao.id, solicitacao.telao);
                  }}
                  disabled={solicitacao.telao === TipoSolicitacaoTelao.PADRAO}
                >
                  SELECIONAR
                </Button>
              </div>
            </div>
          ))}
        </TeloesLinks>
      );
    },
    [handleSubmitTelaoPadrao],
  );

  const handleLinksTeloes = useCallback(
    async solicitacao => {
      try {
        const response = await api.get('/telaopadraofile');
        if (response && response.data) {
          const teloes = response.data;
          showModal({
            title: 'TELÕES DISPONÍVEIS',
            component: renderLinksTeloes(teloes, solicitacao),
            width: '50vw',
          });
        }
      } catch (e) {
        if (e instanceof Error) toast.error(e.message);
        else if (e instanceof AppError) toast.error(e.message);
        else {
          const appError = e as AppError;
          if (appError.message) toast.error(appError.message);
          else toast.error('Erro inesperado');
        }
      }
    },
    [renderLinksTeloes, showModal],
  );

  const renderOpcoesTelao = useCallback(
    solicitacao => {
      return (
        <NovoInfo>
          <p>
            Se o seu evento tem um telão ou caixas de som, podemos aproveitar
            esse espaço para reforçar a nossa marca ou promover nossos
            programas!
          </p>
          <p>
            Utilize as opções abaixo para encontrar (ou solicitar) o melhor
            filme para cada ocasião.
          </p>
          <p>
            Em caso de dúvidas, por favor envie um e-mail para{' '}
            <span>patriciahoca@rpc.com.br</span>, ou entre em contato pelo ramal{' '}
            <span>7952</span>
          </p>
          <p>Agora, vamos lá!</p>
          <div>
            <Button
              color="success"
              onClick={() => {
                handleLinksTeloes(solicitacao);
              }}
            >
              Telão Padrão (1920 x 1080)
            </Button>
            <Button
              color="flag"
              onClick={() => {
                navigate(
                  `/solicitacaopromo/${solicitacao.id}/solicitacaotelao/${TipoSolicitacaoTelao.OUTROFORMATO}`,
                );
                hideModal();
              }}
            >
              Outros Formatos
            </Button>
            <Button
              id="novacriacao"
              color="secondary"
              onClick={() => {
                navigate(
                  `/solicitacaopromo/${solicitacao.id}/solicitacaotelao/${TipoSolicitacaoTelao.NOVACRIACAO}`,
                );
                hideModal();
              }}
            >
              Novas Criações
              <Tooltip target="novacriacao">
                Clique se já alinhou essa produção com a Promo
              </Tooltip>
            </Button>
          </div>
        </NovoInfo>
      );
    },
    [handleLinksTeloes, hideModal, navigate],
  );

  // handle das opcoes de telao
  const handleTeloes = useCallback(
    async solicitacao => {
      showModal({
        width: '65vw',
        title: 'SOLICITAÇÕES DE TELÃO PROMO',
        component: renderOpcoesTelao(solicitacao),
      });
    },
    [renderOpcoesTelao, showModal],
  );

  const handleUpload = useCallback(
    async (solicitacaopromo: SolicitacaoPromo) => {
      setSelected(solicitacaopromo);
      // crio um elemento input para receber o arquivo, com type, accpet, multiple.
      // quando o input muda no onchange, pego o arquivo e verifico o tipo se é mxf e senao for mostro um erro.
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = '.mp4,.MP4';
      input.multiple = false;

      input.onchange = async (e: any) => {
        const file = e.target.files[0];
        if (file) {
          console.log(!file.name.toLowerCase().endsWith('.mp4'));
          if (!file.name.toLowerCase().endsWith('.mp4')) {
            toast.error('Apenas arquivos MP4 são permitidos');
            return;
          }

          const formData = new FormData();
          formData.append('file', file);

          try {
            const response = await api.post(
              `/telaoproduzidofile/${solicitacaopromo.solicitacaotelao.id}`,
              formData,
              {
                timeout: 300000, // 5 minutos
                // monta a barra de porcentagem
                onUploadProgress: e => {
                  const progress = e.total
                    ? Math.round((e.loaded * 100) / e.total)
                    : 0;
                  setUploadProgress(progress);
                },
              },
            );
            if (response?.data) {
              toast.success('Vídeo do telão carregado com sucesso');
              toast.success(
                'O e-mail com o acesso ao vídeo do telão será enviado em breve',
              );
            }
          } catch (error) {
            if (error instanceof Error) {
              toast.error(error.message);
            } else {
              const appError = error as AppError;
              if (appError.message === 'erro') {
                toast.error('Apenas arquivos MP4 são permitidos');
              } else {
                toast.error('Erro ao fazer upload do arquivo');
              }
            }
          }
        }
      };
      input.click();
    },
    [],
  );
  /**
   * USE EFFECT
   */
  useEffect(() => {
    const controller = new AbortController();
    async function loadSolicitacoes() {
      try {
        // Validação em caso de busca por data (usando > ou <)
        if (filtro && (filtro.startsWith('>') || filtro.startsWith('<'))) {
          const parsedDate = parse(
            filtro.replace('<', '').replace('>', ''),
            'P',
            new Date(),
            { locale: pt },
          );
          if (!isValid(parsedDate)) {
            toast.error('Data inválida');
            return;
          }
        }
        setLoading(true);
        const response = await api.get('/solicitacaopromo', {
          signal: controller.signal,
          params: {
            ...(filtro && { search: filtro }),
            status,
            page,
          },
        });
        if (response && response.data) {
          setSolicitacoes(response.data.solicitacoes);
          setPages(response.data.pages);
          setWorkflow({
            emprocesso: response.data.emprocesso,
            enviada: response.data.enviada,
          });
        }
      } catch (e) {
        if (e instanceof Error) toast.error(e.message);
        else if (e instanceof AppError) toast.error(e.message);
        else {
          const appError = e as AppError;
          if (appError.message) toast.error(appError.message);
          else toast.error('Erro inesperado');
        }
      }
      setLoading(false);
    }
    loadSolicitacoes();
    return () => {
      controller.abort();
    };
  }, [filtro, page, status]);

  useEffect(() => {
    if (callbackDialog && callbackDialog.success) {
      if (callbackDialog.success.action === 'duplicapromo' && selected) {
        // atualiza adicionando no workflow
        setWorkflow(prevState => ({
          ...prevState,
          emprocesso: prevState.emprocesso + 1,
        }));
        // Atualiza tabela adicionando o duplicado

        if (status === StatusSolicitacao.EMPROCESSO)
          setSolicitacoes(prevState => [
            ...prevState,
            callbackDialog.success.data,
          ]);
      } else if (callbackDialog.success.action === 'success' && selected) {
        // Atualiza widgets removendo a solicitacao deletada
        setWorkflow(prevState => ({
          emprocesso:
            selected.status === StatusSolicitacao.EMPROCESSO
              ? prevState.emprocesso - 1
              : prevState.emprocesso,
          enviada:
            selected.status === StatusSolicitacao.ENVIADA
              ? prevState.enviada - 1
              : prevState.enviada,
        }));
        // Atualiza tabela removendo a solicitacao deletada
        setSolicitacoes(prevState =>
          prevState.filter(s => s.id !== selected.id),
        );
      }
    }
  }, [callbackDialog, selected, status]);
  return (
    <Container>
      <WidgetContent>
        <Widget
          title="Em processo"
          type="condensed"
          icon={<FiBookOpen />}
          showLink={false}
          active={status === StatusSolicitacao.EMPROCESSO}
          color="info"
          loading={loading}
          handleClick={() => handleClick(StatusSolicitacao.EMPROCESSO)}
          data={workflow.emprocesso}
        />
        <Widget
          title="Enviada"
          type="condensed"
          icon={<FiCheckCircle />}
          active={status === StatusSolicitacao.ENVIADA}
          color="success"
          loading={loading}
          handleClick={() => handleClick(StatusSolicitacao.ENVIADA)}
          data={workflow.enviada}
        />
      </WidgetContent>
      <Card>
        <header>
          <h4>LISTA DE SOLICITAÇÕES</h4>
          <Pagination
            total={pages}
            activePage={page}
            onClick={page => setPage(page)}
          />
          <Button
            color="primary"
            href={'/solicitacaopromo/novo'}
            tooltip="Criar nova Solicitação"
          >
            NOVA SOLICITAÇÃO
            <FiPlus />
          </Button>
          <div>
            <Form
              onSubmit={e => {
                setFiltro(e.searchLocal);
                setPage(1);
              }}
            >
              <Input
                id="filtro"
                name="searchLocal"
                placeholder="Buscar na Tabela"
                icon={<FiSearch />}
              />
              <Tooltip target="filtro" position="bottom">
                Buscas possíveis:
                <br />
                Pelo nome ou pela emissora.
                <br />
                Pela <b> data de inicio de veiculação </b> usando os operadores
                &lt; e &gt;
                <br />
                Ex: &gt;01/01/2024 trará as solicitações que a
                <b> data de inicio de veiculação </b> sejam a partir de
                01/01/2024
              </Tooltip>
            </Form>
          </div>
        </header>
        {loading ? (
          <Loader type="solicitacao_promo" />
        ) : solicitacoes && solicitacoes.length ? (
          <div style={{ padding: `0 15px` }}>
            <Table>
              <thead>
                <tr>
                  <th style={{ width: 850 }}>NOME</th>
                  <th style={{ width: 100 }}>DATA INÍCIO</th>
                  <th style={{ width: 100 }}>DATA VEIC.</th>
                  <th style={{ width: 130 }}>PRAÇA</th>
                  <th style={{ width: 100, textAlign: 'center' }}>TELÃO</th>
                  <th style={{ width: 100, textAlign: 'center' }}>
                    SOLICITANTE
                  </th>
                  <th style={{ textAlign: 'right', width: 80 }}>AÇÕES</th>
                </tr>
              </thead>
              <tbody>
                {solicitacoes.map((s, index) => (
                  <tr key={s.id}>
                    <td>{s.nome}</td>
                    <td>
                      {format(
                        addHours(new Date(s.datainicio), 3),
                        'dd/MM/yyyy',
                      )}
                    </td>
                    <td>
                      {format(
                        addHours(new Date(s.datainicioveiculacao), 3),
                        'dd/MM/yyyy',
                      )}
                    </td>
                    <td>{s.emissora || '-'}</td>
                    <td>
                      {uploadProgress !== 0 &&
                      uploadProgress !== 100 &&
                      s.id === selected.id ? (
                        <ProgressBar
                          uploadProgress={uploadProgress}
                          color="primary"
                        />
                      ) : (
                        <Flag
                          name={s.telao}
                          type={s.telao}
                          handleAction={() => {
                            s.telao === TipoSolicitacaoTelao.NA ||
                            s.telao === TipoSolicitacaoTelao.PADRAO
                              ? handleTeloes(s)
                              : handleUpload(s);
                          }}
                          handleAction2={() => handleLinksTeloes(s)}
                          icon={
                            s.telao === TipoSolicitacaoTelao.NA ||
                            s.telao === TipoSolicitacaoTelao.PADRAO ? (
                              <FiPlus />
                            ) : hasUploadTelao ? (
                              <FiUpload />
                            ) : null
                          }
                          icon2={
                            s.telao === TipoSolicitacaoTelao.PADRAO ? (
                              <FiEdit />
                            ) : null
                          }
                        />
                      )}
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <Avatar
                        size="40"
                        name={s.solicitante?.nome}
                        src={s.solicitante?.avatar_url}
                        round
                      />
                    </td>
                    <td style={{ justifyItems: 'flex-end' }}>
                      <div style={{ display: 'flex' }}>
                        <Button
                          id={`id_visualizar_${s.id}`}
                          fill
                          color="font"
                          round
                          disabled={s.status === StatusSolicitacao.EMPROCESSO}
                          onClick={e => {
                            e.preventDefault();
                            window.open(
                              `${config.API_URL}/solicitacaopromofile/${s.id}`,
                              '_blank',
                            );
                          }}
                        >
                          <FiEye />
                        </Button>
                        <Tooltip target={`id_visualizar_${s.id}`}>
                          VISUALIZAR
                        </Tooltip>
                        <DropDownMenu
                          id={`acoes${index}`}
                          disabled={false}
                          actions={[
                            {
                              value: 0,
                              label: 'EDITAR',
                              disabled:
                                (s.solicitante.id !== profile.id &&
                                  !hasSolPromoOutros) ||
                                s.status === StatusSolicitacao.ENVIADA,
                              icon: <FiEdit />,
                              href: `/solicitacaopromo/${s.id}`,
                            },
                            {
                              value: 1,
                              label: 'DUPLICAR',
                              disabled: s.status === StatusSolicitacao.ENVIADA,
                              icon: <FiCopy />,
                            },
                            {
                              value: 2,
                              label: 'APAGAR',
                              disabled:
                                s.status === StatusSolicitacao.ENVIADA ||
                                (s.solicitante.id !== profile.id &&
                                  !hasSolPromoOutros),
                              icon: <FiTrash2 />,
                            },
                          ]}
                          handleClick={action =>
                            !action.disabled && handleActionSelect(s, action)
                          }
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        ) : (
          <NotFound img={NoSolicitacaoPromo} title="Solicitação Promo">
            Nenhuma Solicitação de Promo encontrada
          </NotFound>
        )}
      </Card>
    </Container>
  );
}
